/**
 * QCE (Qualitative Coding Engine) redux slice — the "Themes (Beta)" data layer.
 *
 * Uses RTK core (`createSlice` + `createAsyncThunk`) over the `qceService`
 * client, read with `useSelector` — the same shape as how the app integrates the
 * node backend. We deliberately do NOT use RTK Query here: this app pairs
 * react-redux v9 with @reduxjs/toolkit v1.9, and RTK Query's hooks require RTK
 * v2 with react-redux v9, so its hooks can't subscribe to the store (they warn
 * "middleware not added" even when it is). Core thunks/selectors are unaffected.
 */
import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { AnyAction, PayloadAction, ThunkDispatch } from "@reduxjs/toolkit";

import type { CodeSelectionDetail } from "../../../Pages/Hub/coding/codeSelectionEvent";
import type { RootState } from "../rootReducer";
import type {
  Theme,
  ThemeWrite,
  ThemeCode,
  ClipAttach,
  Exemplar,
  ThemeDraft,
  HighlightCreate,
  AutoDetectConfig,
  AutoDetectRunRequest,
} from "../../../Pages/Project/Tabs/ThemesTab/types";

import { qceService } from "./qceService";
import type { CodePatch } from "./qceService";

type Status = "idle" | "loading" | "succeeded" | "failed";

// Stable empty reference so selectors don't return a fresh [] each render.
const EMPTY_CODES: ThemeCode[] = [];

export type PendingSelectionAction = "add-to-theme" | "highlight";

export interface PendingSelection {
  detail: CodeSelectionDetail;
  action: PendingSelectionAction;
}

export interface QceState {
  // Themes list keyed by scope (projectId, or "__global__" for the org-wide tab)
  // so two consumers with different scopes never contend over a single slot (the
  // Auto-detect memory-leak fix). Each scope fetches once and reads its own data.
  themesByScope: Record<string, Theme[]>;
  themesStatusByScope: Record<string, Status>;
  codesByTheme: Record<string, ThemeCode[]>;
  codesStatus: Record<string, Status>;
  storyCodes: Record<string, ThemeCode[]>;
  pendingSelection: PendingSelection | null;
  /** Hub "Show highlights" overlay toggle — shared so creating a highlight can auto-enable it. */
  showHighlights: boolean;
  // Staleness guards: the requestId of the most recently *dispatched* read for
  // each key. Mutations (add/delete/move) fire several overlapping refetches;
  // under network latency an earlier (pre-mutation) response can resolve last
  // and clobber the store with stale data. We apply a fulfilled read only when
  // it is still the newest in flight, so the final state always reflects the
  // last dispatched fetch. (Works locally where responses resolve in order;
  // manifested on staging as "moved highlight not removed / duplicated".)
  themesReqIdByScope: Record<string, string>;
  codesReqId: Record<string, string>;
  storyCodesReqId: Record<string, string>;
}

const initialState: QceState = {
  themesByScope: {},
  themesStatusByScope: {},
  codesByTheme: {},
  codesStatus: {},
  storyCodes: {},
  pendingSelection: null,
  showHighlights: false,
  themesReqIdByScope: {},
  codesReqId: {},
  storyCodesReqId: {},
};

const tokenOf = (getState: () => unknown) => (getState() as RootState).auth?.token;
// Per-scope themes cache key: a projectId, or "__global__" for the org-wide (null) scope.
const GLOBAL_SCOPE = "__global__";
const scopeKey = (projectId: string | null | undefined) => projectId ?? GLOBAL_SCOPE;
const EMPTY_THEMES: Theme[] = [];
const loadedScopeKeys = (getState: () => unknown) => Object.keys((getState() as RootState).qce.themesStatusByScope);
// The project scope currently loaded (non-global) — used to assign a new theme's projectId.
const activeProjectScope = (getState: () => unknown): string | undefined =>
  loadedScopeKeys(getState).find((k) => k !== GLOBAL_SCOPE);
// Refetch every currently-loaded scope so counts stay fresh across the project tab
// and any open global/modal view (each scope is independent).
const refetchLoadedScopes = (getState: () => unknown, dispatch: ThunkDispatch<unknown, unknown, AnyAction>) => {
  for (const k of loadedScopeKeys(getState)) dispatch(fetchThemes(k === GLOBAL_SCOPE ? undefined : k));
};

// ----- Reads -----
// `projectId` scopes the list to one project (project tab); omit for org-wide (global tab).
export const fetchThemes = createAsyncThunk("qce/fetchThemes", (projectId: string | undefined, { getState }) =>
  qceService.listThemes(tokenOf(getState), projectId)
);

export const fetchThemeCodes = createAsyncThunk("qce/fetchThemeCodes", (themeId: string, { getState }) =>
  qceService.listThemeCodes(tokenOf(getState), themeId)
);

export const fetchStoryCodes = createAsyncThunk("qce/fetchStoryCodes", (storyId: string, { getState }) =>
  qceService.listStoryCodes(tokenOf(getState), storyId)
);

// ----- Theme mutations (refetch themes for fresh counts) -----
export const createTheme = createAsyncThunk("qce/createTheme", async (body: ThemeWrite, { getState, dispatch }) => {
  // U2b: assign the current project scope unless the caller set one explicitly.
  const scope = activeProjectScope(getState);
  const payload = scope && body.projectId === undefined ? { ...body, projectId: scope } : body;
  const t = await qceService.createTheme(tokenOf(getState), payload);
  refetchLoadedScopes(getState, dispatch);
  return t;
});

export const updateTheme = createAsyncThunk(
  "qce/updateTheme",
  async ({ id, body }: { id: string; body: ThemeWrite }, { getState, dispatch }) => {
    const t = await qceService.updateTheme(tokenOf(getState), id, body);
    refetchLoadedScopes(getState, dispatch);
    return t;
  }
);

export const deleteTheme = createAsyncThunk("qce/deleteTheme", async (id: string, { getState, dispatch }) => {
  const r = await qceService.deleteTheme(tokenOf(getState), id);
  refetchLoadedScopes(getState, dispatch);
  return r;
});

export const generateThemeFromExamples = createAsyncThunk(
  "qce/generateTheme",
  (exemplars: Exemplar[], { getState }): Promise<ThemeDraft> =>
    qceService.generateThemeFromExamples(tokenOf(getState), exemplars)
);

export const attachClips = createAsyncThunk(
  "qce/attachClips",
  async ({ themeId, clips }: { themeId: string; clips: ClipAttach[] }, { getState, dispatch }) => {
    const r = await qceService.attachClips(tokenOf(getState), themeId, clips);
    refetchLoadedScopes(getState, dispatch);
    dispatch(fetchThemeCodes(themeId));
    return r;
  }
);

// ----- Code mutations -----
export const updateCode = createAsyncThunk(
  "qce/updateCode",
  async (
    { codeId, patch, themeId, storyId }: { codeId: string; patch: CodePatch; themeId?: string; storyId?: string },
    { getState, dispatch }
  ) => {
    const r = await qceService.updateCode(tokenOf(getState), codeId, patch);
    if (themeId) dispatch(fetchThemeCodes(themeId));
    if (storyId) dispatch(fetchStoryCodes(storyId));
    return r;
  }
);

export const deleteCode = createAsyncThunk(
  "qce/deleteCode",
  async ({ codeId, themeId }: { codeId: string; themeId?: string }, { getState, dispatch }) => {
    const r = await qceService.deleteCode(tokenOf(getState), codeId);
    refetchLoadedScopes(getState, dispatch);
    if (themeId) dispatch(fetchThemeCodes(themeId));
    return r;
  }
);

export const attachHighlight = createAsyncThunk(
  "qce/attachHighlight",
  async (body: HighlightCreate, { getState, dispatch }) => {
    const r = await qceService.attachHighlight(tokenOf(getState), body);
    refetchLoadedScopes(getState, dispatch);
    // Refresh the story's codes so the Hub overlay paints the new highlight
    // immediately (D4), without waiting for a manual toggle/refetch.
    dispatch(fetchStoryCodes(body.story_id));
    return r;
  }
);

export const updateExcerptNote = createAsyncThunk(
  "qce/updateExcerptNote",
  async (
    { excerptId, note, storyId }: { excerptId: string; note: string; storyId?: string },
    { getState, dispatch }
  ) => {
    const r = await qceService.updateExcerptNote(tokenOf(getState), excerptId, note);
    if (storyId) dispatch(fetchStoryCodes(storyId));
    return r;
  }
);

export const addExcerptTheme = createAsyncThunk(
  "qce/addExcerptTheme",
  async ({ excerptId, themeId }: { excerptId: string; themeId: string }, { getState, dispatch }) => {
    const r = await qceService.addExcerptTheme(tokenOf(getState), excerptId, themeId);
    refetchLoadedScopes(getState, dispatch);
    return r;
  }
);

// ----- Auto-detection config (C5 / B6) -----
export const fetchThemeConfig = createAsyncThunk("qce/fetchThemeConfig", (projectId: string, { getState }) =>
  qceService.getThemeConfig(tokenOf(getState), projectId)
);

export const saveThemeConfig = createAsyncThunk(
  "qce/saveThemeConfig",
  ({ projectId, autoDetect }: { projectId: string; autoDetect: AutoDetectConfig }, { getState }) =>
    qceService.putThemeConfig(tokenOf(getState), projectId, autoDetect)
);

// Run detection on existing recordings; refetch themes so card counts update.
export const runThemeDetection = createAsyncThunk(
  "qce/runThemeDetection",
  async ({ projectId, body }: { projectId: string; body: AutoDetectRunRequest }, { getState, dispatch }) => {
    const r = await qceService.runThemeConfig(tokenOf(getState), projectId, body);
    refetchLoadedScopes(getState, dispatch);
    return r;
  }
);

const qceSlice = createSlice({
  name: "qce",
  initialState,
  reducers: {
    setPendingSelection(s, a: PayloadAction<PendingSelection>) {
      s.pendingSelection = a.payload;
    },
    clearPendingSelection(s) {
      s.pendingSelection = null;
    },
    setShowHighlights(s, a: PayloadAction<boolean>) {
      s.showHighlights = a.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchThemes.pending, (s, a) => {
        const k = a.meta.arg ?? GLOBAL_SCOPE;
        s.themesStatusByScope[k] = "loading";
        s.themesReqIdByScope[k] = a.meta.requestId;
      })
      .addCase(fetchThemes.fulfilled, (s, a) => {
        const k = a.meta.arg ?? GLOBAL_SCOPE;
        if (a.meta.requestId !== s.themesReqIdByScope[k]) return; // superseded by a newer fetch
        s.themesStatusByScope[k] = "succeeded";
        s.themesByScope[k] = a.payload || [];
      })
      .addCase(fetchThemes.rejected, (s, a) => {
        const k = a.meta.arg ?? GLOBAL_SCOPE;
        if (a.meta.requestId !== s.themesReqIdByScope[k]) return;
        s.themesStatusByScope[k] = "failed";
      })
      .addCase(fetchThemeCodes.pending, (s, a) => {
        s.codesStatus[a.meta.arg] = "loading";
        s.codesReqId[a.meta.arg] = a.meta.requestId;
      })
      .addCase(fetchThemeCodes.fulfilled, (s, a) => {
        if (a.meta.requestId !== s.codesReqId[a.meta.arg]) return; // superseded
        s.codesStatus[a.meta.arg] = "succeeded";
        s.codesByTheme[a.meta.arg] = a.payload || [];
      })
      .addCase(fetchThemeCodes.rejected, (s, a) => {
        if (a.meta.requestId !== s.codesReqId[a.meta.arg]) return;
        s.codesStatus[a.meta.arg] = "failed";
      })
      .addCase(fetchStoryCodes.pending, (s, a) => {
        s.storyCodesReqId[a.meta.arg] = a.meta.requestId;
      })
      .addCase(fetchStoryCodes.fulfilled, (s, a) => {
        if (a.meta.requestId !== s.storyCodesReqId[a.meta.arg]) return; // superseded
        s.storyCodes[a.meta.arg] = a.payload || [];
      });
  },
});

export const { setPendingSelection, clearPendingSelection, setShowHighlights } = qceSlice.actions;

// ----- Typed dispatch + convenience hooks -----
type QceDispatch = ThunkDispatch<RootState, unknown, AnyAction>;

export const useQceDispatch = () => useDispatch<QceDispatch>();

/**
 * Themes list — fetches on mount and whenever the project scope changes (U2b).
 * Pass a `projectId` for a project tab (only that project's themes); omit it for
 * the org-wide global tab.
 */
export function useThemes(projectId?: string) {
  const dispatch = useQceDispatch();
  const key = scopeKey(projectId);
  const themes = useSelector((s: RootState) => s.qce.themesByScope[key] ?? EMPTY_THEMES);
  const status = useSelector((s: RootState): Status => s.qce.themesStatusByScope[key] ?? "idle");
  useEffect(() => {
    // Fetch this scope exactly once. Each scope owns its status, so mounting a
    // second consumer with a DIFFERENT scope never flips this one back to idle —
    // no cross-scope refetch ping-pong (the Auto-detect memory-leak fix).
    if (status === "idle") dispatch(fetchThemes(projectId));
  }, [status, key, projectId, dispatch]);
  return {
    themes,
    loading: status === "idle" || status === "loading",
    error: status === "failed",
    refetch: () => dispatch(fetchThemes(projectId)),
  };
}

/** A theme's codes — refetches when the theme id changes. */
export function useThemeCodes(themeId: string) {
  const dispatch = useQceDispatch();
  const codes = useSelector((s: RootState) => s.qce.codesByTheme[themeId]);
  const status = useSelector((s: RootState) => s.qce.codesStatus[themeId]);
  useEffect(() => {
    dispatch(fetchThemeCodes(themeId));
  }, [themeId, dispatch]);
  return {
    codes: codes || EMPTY_CODES,
    loading: (status === "loading" || status === undefined) && !codes,
    error: status === "failed",
  };
}

/** A story's codes for the Hub overlay — fetches when active. */
export function useStoryCodes(storyId: string | undefined, active: boolean) {
  const dispatch = useQceDispatch();
  const codes = useSelector((s: RootState) => (storyId ? s.qce.storyCodes[storyId] : undefined));
  useEffect(() => {
    if (active && storyId) dispatch(fetchStoryCodes(storyId));
  }, [active, storyId, dispatch]);
  return active && storyId ? codes || EMPTY_CODES : EMPTY_CODES;
}

/** Hub "Show highlights" overlay toggle, shared across the transcript + coding controller. */
export function useShowHighlights(): [boolean, (next: boolean) => void] {
  const dispatch = useQceDispatch();
  const showHighlights = useSelector((s: RootState) => s.qce.showHighlights);
  return [showHighlights, (next: boolean) => dispatch(setShowHighlights(next))];
}

export default qceSlice.reducer;
